<template>
  <div>
    <v-row class="align-stretch pt-3" dense>
      <slot name="label">
        <v-col cols="6" sm="6" :md="labelSize" v-if="label && !hideLabel">
          <v-card class="d-flex" flat>
            <v-tooltip v-if="nullable" top>
              <template #activator="{ on }">
                <v-card v-on="on" height="32" flat>
                  <v-switch v-model="nullValue" dense class="mt-0"> </v-switch>
                </v-card>
              </template>
              <span>{{ nullValue ? "Disable" : "Enable" }}</span>
            </v-tooltip>
            <v-card flat>
              <span class="text-subtitle-2">{{ label }}</span>
              <div v-if="hint">
                <template v-if="hint.length > 80">
                  <v-tooltip top max-width="300">
                    <template #activator="{ on }">
                      <span class="text-caption" v-on="on">
                        {{ hint.substring(0, 80) + "..." }}
                      </span>
                    </template>
                    <span>{{ hint }}</span>
                  </v-tooltip>
                </template>
                <span v-else class="text-caption">{{ hint }}</span>
              </div>
            </v-card>
          </v-card>
        </v-col>
      </slot>
      <v-spacer></v-spacer>
      <template v-if="!nullable || (nullable && nullValue)">
        <v-col v-if="chartable && !chartVisible" class="shrink">
          <Dialog
            icon="show_chart"
            tooltip="Show Trend"
            :title="chartTitle"
            :loading="chartProps.loading || false"
            :body="{ component: chartComponent, props: chartProps }"
            @open="$emit('open-chart')"
            @close="$emit('close-chart')"
          ></Dialog>
        </v-col>
        <v-col v-if="tableInput" class="shrink">
          <Dialog
            icon="edit"
            tooltip="Edit values"
            :width="500"
            :title="chartTitle"
            :body="{
              component: inputTableComponent,
              props: { value: computedValue, num_years: num_years, startYear: startYear },
            }"
            v-on="$listeners"
          ></Dialog>
        </v-col>
        <v-col v-if="spreadValues" class="shrink">
          <v-tooltip top>
            <template #activator="{ on }">
              <v-btn icon color="primary" v-on="on" @click="repeatFirst" small>
                <v-icon>content_copy</v-icon>
              </v-btn>
            </template>
            <span>Repeat first year</span>
          </v-tooltip>
        </v-col>
        <v-col v-if="extraButton" class="shrink">
          <slot name="extraButton"></slot>
        </v-col>
        <v-col v-if="!spreadValues" class="shrink">
          <v-tooltip top>
            <template #activator="{ on }">
              <v-btn-toggle v-model="repeat" color="primary">
                <v-btn small icon v-on="on" :value="true">
                  <v-icon color="primary">mdi-repeat</v-icon>
                </v-btn>
              </v-btn-toggle>
            </template>
            <span>Active: same value for all years</span>
          </v-tooltip>
        </v-col>
      </template>
      <v-col v-if="!chartVisible" class="grow">
        <slot name="inputPrepend"></slot>
        <v-row align="baseline" style="overflow-x: auto">
          <template v-if="this.value">
            <v-col
              v-for="(year, index) in numInputs"
              :key="year"
              class="py-0 mx-1"
              style="min-width: 150px"
            >
              <v-card flat class="d-flex">
                <slot
                  v-if="!nullable || (nullable && nullValue)"
                  :year="year"
                  :value="value"
                  :suffix="suffix"
                  :type="type"
                  :readonly="readonly"
                  :error-messages="
                    errorMessages && errorMessages[index] ? errorMessages[index].messages : null
                  "
                  :label="inputLabel(year)"
                >
                  <v-text-field
                    v-model.number="computedValue[year - 1]"
                    :error-messages="
                      errorMessages != null && errorMessages[index] != null
                        ? errorMessages[index].messages
                        : null
                    "
                    :label="inputLabel(year)"
                    :suffix="suffix"
                    :type="type"
                    :readonly="readonly"
                    :min="min"
                    @input="setYearValue($event, year - 1)"
                  >
                  </v-text-field>
                </slot>
              </v-card>
            </v-col>
          </template>
        </v-row>
      </v-col>
    </v-row>
    <v-row v-if="chartVisible" no-gutters>
      <!-- Plot -->
      <v-col cols="12" sm="12" v-if="chartable">
        <PlotlyWrapper :data="computedChartData" :layout="computedChartLayout"></PlotlyWrapper>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import Chart from "@/components/charts/ChartJs.vue";
import InputTable from "@/components/InputTable.vue";

export default {
  name: "InputYearly",
  components: {
    Dialog: () => import("@/components/Dialog.vue"),
    PlotlyWrapper: () => import("@/components/charts/PlotlyWrapper.vue"),
  },
  props: {
    errorMessages: {
      type: [Object, Array],
      required: false,
      default: () => ({}),
    },
    label: {
      type: String,
      required: false,
    },
    hideLabel: {
      type: Boolean,
      required: false,
      defalut: false,
    },
    caption: {
      type: String,
      required: false,
    },
    suffix: {
      type: String,
      required: false,
    },
    value: {
      type: Array,
      required: false,
      default() {
        return [];
      },
    },
    profileType: {
      type: String,
      required: false,
      default: "",
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false,
    },
    type: {
      type: String,
      required: false,
      default: "text",
    },
    num_years: {
      type: Number,
      required: false,
      default: 2,
    },
    startYear: {
      type: [Number, null],
      required: false,
      default: null,
    },
    hint: {
      type: String,
      required: false,
      default: null,
    },
    chart: {
      type: Object,
      required: false,
      default: () => null,
    },
    tableInput: {
      type: Boolean,
      required: false,
      default: true,
    },
    fill: {
      type: Boolean,
      required: false,
      default: false,
    },
    fillValue: {
      type: Number,
      required: false,
      default: null,
    },
    min: {
      type: [Number, null],
      required: false,
      default: null,
    },
    labelSize: {
      type: Number,
      required: false,
      default: 4,
    },
    extraButton: {
      type: Boolean,
      required: false,
      default: false,
    },
    nullable: {
      type: Boolean,
      required: false,
      default: false,
    },
    spreadValues: { type: Boolean, required: false, default: false },
    objectValue: {
      type: Boolean,
      required: false,
      default: false,
    },
    availableYears: {
      type: Number,
      required: false,
      default: null,
    },
    chartVisible: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      nullValue: !(this.value === null),
      viewChart: false,
      repeat: false,
      isRepeatSet: false,
      chartOptions: {
        responsive: true,
        legend: {
          labels: {
            fontSize: 20,
          },
        },
        scales: {
          xAxes: [
            {
              display: true,
              scaleLabel: {
                display: true,
                labelString: "Year",
                fontSize: 18,
              },
            },
          ],
          yAxes: [
            {
              display: true,
              scaleLabel: {
                display: true,
                labelString: `${this.suffix}`,
                fontSize: 18,
              },
            },
          ],
        },
        zoom: {
          enabled: false,
          mode: "xy",
        },
        pan: {
          enabled: false,
          mode: "xy",
        },
        plugins: {
          datalabels: {
            display: false,
          },
        },
      },
    };
  },
  computed: {
    computedValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.emitChange(val);
      },
    },
    inputTableComponent() {
      return InputTable;
    },
    chartable() {
      return this.type === "number" || this.chart;
    },
    chartTitle() {
      if (this.chart && this.chart.title) return this.chart.title;
      return this.label;
    },
    chartComponent() {
      if (this.chart && this.chart.component) return this.chart.component;
      return Chart;
    },
    chartProps() {
      if (this.chart && this.chart.props) return this.chart.props;
      return {
        type: "line",
        data: this.chartData,
        options: this.chartOptions,
      };
    },
    // For older Chart
    chartData() {
      const data = this.value;
      const color = this.$utils.getChartColor((Math.random() * 10).toFixed(0));

      return {
        labels: [...data.keys()].map((e) => e + 1),
        datasets: [
          {
            data,
            label: this.label,
            fill: false,
            borderColor: color,
            backgroundColor: color,
          },
        ],
      };
    },
    // For PlotlyWrapper
    computedChartData() {
      return [
        {
          x: Array.from({ length: this.num_years }, (_, index) => this.startYear + index),
          y: this.value,
          type: "bar",
        },
      ];
    },
    computedChartLayout() {
      return {
        xaxis: {
          title: "Year",
          tickmode: "linear",
          tick0: this.startYear,
          dtick: 1,
        },
        yaxis: {
          title: this.suffix,
        },
      };
    },
    numInputs() {
      if (this.repeat && !this.spreadValues) {
        return 1;
      }
      return this.num_years;
    },
  },
  watch: {
    num_years() {
      this.fillValues();
    },
    nullValue(val) {
      if (!val) {
        this.$emit("input", null);
      } else if (this.value == null || !Array.isArray(this.value)) {
        this.$emit("input", []);
      } else if (this.value && !this.value.length && this.fill) {
        this.$emit("input", Array(this.num_years).fill(this.fillValue));
      }
    },
    nullable(v) {
      // If changes to false, then we should emit an
      // empty array for the inputinstead of being null.
      if (!v) {
        this.$emit("input", []);
      }
    },
    repeat(v) {
      if (v) {
        this.repeatFirst();
      }
      this.$emit("update:repeat", v);
      this.$emit("change");
    },
  },
  created() {
    if (this.value == null || !Array.isArray(this.value)) {
      this.$emit("input", this.nullable ? null : []);
    }
    if (this.value && !this.value.length && this.fill) {
      this.$emit("input", Array(this.num_years).fill(this.fillValue));
    }
    this.fillValues();
  },
  methods: {
    closeChart() {
      this.viewChart = false;
    },
    fillValues() {
      if (this.value) {
        let valueCopy = this.value;
        if (this.num_years > this.value.length && this.fill) {
          const itemsToAppend = Array(this.num_years - this.value.length).fill(
            this.fillValue !== null ? this.fillValue : this.value[this.value.length - 1]
          );
          valueCopy = [...this.value, ...itemsToAppend];
          this.$emit("input", valueCopy);
        }
        if (
          valueCopy.every((val, _i, arr) => val === arr[0]) &&
          valueCopy.length === this.num_years &&
          !this.spreadValues
        ) {
          this.repeat = true;
        }
      }
    },
    repeatFirst() {
      let emitValue;
      if (this.availableYears === null) {
        let firstValue = this.value[0];
        if (firstValue) {
          if (Object.prototype.hasOwnProperty.call(firstValue, "id")) {
            firstValue = firstValue.id;
          } else if (Object.prototype.hasOwnProperty.call(firstValue, "value")) {
            firstValue = firstValue.value;
          }
        }
        emitValue = Array(this.num_years).fill(firstValue);
      } else {
        const availableYears =
          this.availableYears > this.num_years ? this.num_years : this.availableYears;
        const nonAvailableYears =
          this.num_years - this.availableYears < 0 ? 0 : this.num_years - this.availableYears;
        emitValue = [
          ...Array(availableYears).fill(this.value[0]),
          ...Array(nonAvailableYears).fill(0),
        ];
      }
      this.$emit("input", emitValue);
    },
    emitChange(val = null) {
      this.$emit("input", val || this.computedValue);
      this.$emit("change");
    },
    setYearValue($event, index) {
      let value = this.computedValue;
      const inputValue = Number($event);
      if (this.repeat) {
        value = Array(value.length).fill(inputValue);
      } else {
        value.splice(index, 1, inputValue);
      }
      this.emitChange(value);
    },
    inputLabel(year) {
      if (this.repeat && !this.spreadValues) {
        return `Same value for all years${
          this.startYear ? `: ${this.startYear} - ${this.startYear + this.num_years - 1}` : ""
        }`;
      }
      return `Year ${year}${this.startYear ? ` - ${this.startYear + year - 1}` : ""}`;
    },
  },
};
</script>

<style scoped>
.btnToggle {
  flex-direction: column;
}
</style>
